import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Search, Flame, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export default function ExerciseDialog({ open, onOpenChange, date, onAdded, bodyWeightKg }) {
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [activity, setActivity] = useState(null);
  const [duration, setDuration] = useState(30);
  const [notes, setNotes] = useState("");
  const [customName, setCustomName] = useState("");
  const [customMet, setCustomMet] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQ(""); setActivity(null); setDuration(30); setNotes(""); setCustomName(""); setCustomMet("");
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/exercise/library`, { headers: authHeaders() });
        setLibrary(res.data || []);
      } catch { toast.error("Failed to load exercises"); }
      finally { setLoading(false); }
    })();
  }, [open]);

  const filtered = useMemo(() => {
    if (!q.trim()) return library;
    const s = q.toLowerCase();
    return library.filter((e) => e.name.toLowerCase().includes(s) || e.category.toLowerCase().includes(s));
  }, [library, q]);

  const grouped = useMemo(() => {
    const map = {};
    for (const e of filtered) {
      (map[e.category] = map[e.category] || []).push(e);
    }
    return map;
  }, [filtered]);

  const estimate = (met, minutes) => Math.round(((met * Math.max(1, bodyWeightKg || 70) * 3.5) / 200) * Math.max(0, minutes));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        date,
        duration_min: Number(duration) || 0,
        notes: notes || null,
      };
      if (activity) {
        payload.activity_key = activity.key;
      } else if (customName.trim() && customMet) {
        payload.activity_name = customName.trim();
        payload.met = Number(customMet);
      } else {
        toast.error("Pick an activity or fill custom name + MET"); setSaving(false); return;
      }
      const res = await axios.post(`${API}/exercise/entries`, payload, { headers: authHeaders() });
      toast.success(`Added ${res.data.activity_name}: ${res.data.calories_burned} kcal`);
      onAdded(res.data);
      onOpenChange(false);
    } catch { toast.error("Failed to add exercise"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col" data-testid="exercise-dialog">
        <DialogHeader>
          <DialogTitle>Add exercise</DialogTitle>
        </DialogHeader>

        {activity ? (
          <div className="space-y-4">
            <button className="text-xs text-emerald-600 hover:underline" onClick={() => setActivity(null)}>← Back to list</button>
            <div className="p-3 rounded-lg bg-orange-50 border border-orange-100">
              <div className="font-semibold">{activity.name}</div>
              <div className="text-xs text-gray-600">{activity.category} · MET {activity.met}</div>
            </div>
            <div>
              <Label className="text-xs">Duration (minutes)</Label>
              <Input type="number" min="1" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="exercise-duration-input" />
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Sets, distance, feeling…" />
            </div>
            <div className="p-3 rounded-lg bg-gradient-to-r from-orange-100 to-amber-100 text-center">
              <div className="text-[11px] uppercase font-bold text-orange-700">Estimated burn</div>
              <div className="text-2xl font-extrabold text-orange-700 tabular-nums flex items-center justify-center gap-1">
                <Flame className="w-5 h-5" /> {estimate(activity.met, Number(duration) || 0)} kcal
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="exercise-save-btn">
                {saving ? "Saving…" : "Add exercise"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="relative shrink-0">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search activities" className="pl-9" data-testid="exercise-search-input" />
            </div>

            <div className="flex-1 overflow-y-auto mt-3 pr-1 space-y-3">
              {loading && <div className="text-center text-sm text-gray-500 py-6"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>}
              {Object.entries(grouped).map(([cat, list]) => (
                <div key={cat}>
                  <div className="text-[11px] uppercase font-bold text-gray-500 mb-1">{cat}</div>
                  <div className="space-y-1">
                    {list.map((e) => (
                      <button
                        key={e.key}
                        onClick={() => setActivity(e)}
                        className="w-full text-left p-2.5 rounded-lg border border-gray-200 hover:border-orange-400 hover:bg-orange-50/40 transition-all flex items-center justify-between"
                        data-testid={`exercise-option-${e.key}`}
                      >
                        <span className="text-sm font-medium">{e.name}</span>
                        <span className="text-[11px] text-gray-500 tabular-nums">MET {e.met}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* Custom activity */}
              <div className="border-t border-dashed pt-3 mt-3">
                <div className="text-[11px] uppercase font-bold text-gray-500 mb-2">Custom activity</div>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Activity name" value={customName} onChange={(e) => setCustomName(e.target.value)} />
                  <Input type="number" step="0.1" placeholder="MET value" value={customMet} onChange={(e) => setCustomMet(e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Input type="number" placeholder="Minutes" value={duration} onChange={(e) => setDuration(e.target.value)} />
                  <Button onClick={save} disabled={!customName || !customMet || saving} className="bg-orange-500 hover:bg-orange-600" data-testid="exercise-custom-save">
                    {saving ? "Saving…" : "Add custom"}
                  </Button>
                </div>
                <div className="text-[10px] text-gray-500 mt-1">MET reference: light = 3, moderate = 5, vigorous = 8+</div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
